import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'
import * as Select from '@radix-ui/react-select'

import { EMPLOYMENT_STATUS } from '@/constants/user-profile'

interface EmploymentStatusSelectorProps {
  value: string
  onChange: (value: string) => void
}
export const EmploymentStatusSelector: React.FC<
  EmploymentStatusSelectorProps
> = ({ value, onChange }) => {
  const { t } = useTranslation()
  const employmentStatuses = [
    {
      value: EMPLOYMENT_STATUS.EMPLOYED,
      label: t('employmentStatusSelector.options.employed'),
    },
    {
      value: EMPLOYMENT_STATUS.SELF_EMPLOYED,
      label: t('employmentStatusSelector.options.selfEmployed'),
    },
    {
      value: EMPLOYMENT_STATUS.RETIRED,
      label: t('employmentStatusSelector.options.retired'),
    },
    {
      value: EMPLOYMENT_STATUS.UNEMPLOYED,
      label: t('employmentStatusSelector.options.unemployed'),
    },
    {
      value: EMPLOYMENT_STATUS.STUDENT,
      label: t('employmentStatusSelector.options.student'),
    },
  ]
  return (
    <Select.Root
      required
      value={value}
      onValueChange={onChange}
      data-testid="form-employmentstatusselector"
    >
      <Select.Trigger
        className="text-pebble dark:text-snow py-2 h-full w-full inline-flex items-end leading-none gap-[5px] min-w-[150px] justify-between data-[placeholder]:text-gray-400 dark:data-[placeholder]:text-gray-400 outline-none border-b-rock border-b-2"
        data-testid="form-employmentstatusselector"
      >
        <Select.Value
          placeholder={t('employmentStatusSelector.placeholder')}
          data-testid="form-employmentstatusselector"
        />
        <Select.Icon
          className="text-gray-400"
          data-testid="form-employmentstatusselector"
        >
          <ChevronDownIcon />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className="bg-white dark:bg-pebble border border-pebble dark:border-white overflow-hidden"
          data-testid="form-employmentstatusselector"
        >
          <Select.ScrollUpButton
            className="p-2.5"
            data-testid="form-employmentstatusselector"
          >
            <ChevronUpIcon />
          </Select.ScrollUpButton>
          <Select.Viewport data-testid="form-employmentstatusselector">
            {employmentStatuses.map((status) => {
              return (
                <Select.Item
                  key={status.value}
                  value={status.value}
                  className="hover:bg-rock min-w-[150px] justify-between p-2 cursor-pointer"
                  data-testid={`employment-status-selector-item-${status.value.toLowerCase()}`}
                >
                  <Select.ItemText
                    data-testid={`employment-status-selector-item-text-${status.value.toLowerCase()}`}
                  >
                    {status.label}
                  </Select.ItemText>
                </Select.Item>
              )
            })}
          </Select.Viewport>
          <Select.ScrollDownButton
            className="p-2.5 text-gray-400"
            data-testid="form-employmentstatusselector"
          >
            <ChevronDownIcon />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

/* 
Potential improvements:
1. Add appropriate aria-labels for better accessibility
2. Add form validation feedback (visual indicators for required selection)
3. Consider adding descriptions or help text for each employment status
4. Add a loading state for async data operations
5. Improve keyboard navigation and focus management
6. Consider adding confirmation or warning when changing employment status affects other form fields
7. Add transition effects for smoother UX
*/
